// subsurface.js — regional subsurface context for a project site.
//
// The third site attribute after elevation and climate, and fetched the same
// way: a keyless point query at the project's coordinates, straight from the
// browser, stored as one jsonb blob on the project.
//
// Two independent lookups per site:
//   soil    — SoilGrids (ISRIC), a global 250 m grid of modelled soil
//             properties. We take sand/silt/clay at 0–5 cm and 100–200 cm and
//             reduce each depth to a USDA texture class ("sandy clay loam"),
//             which is the form an architect actually reads.
//   bedrock — Macrostrat, which returns the mapped geologic unit at a point:
//             lithology ("basalt"), unit name and age.
//
// SCOPE — this is REGIONAL CONTEXT, NOT SITE DATA. A 250 m soil grid and a
// 1:250k–1:1M geological map tell you "Deccan basalt" or "marine clay over
// sand" for the area; they cannot tell you what is under one plot. Nothing here
// substitutes for a borehole log or a soil investigation, and the UI labels it
// as such. Treat a filled-in value as a conversation starter, never as an input
// to a foundation decision.
//
// The two sources are independent: a site keeps whichever half resolved, so a
// bedrock hit is still recorded when the soil service is down (and vice versa).
//
// fetchSubsurface([{ id, lat, lng }]) →
//   Promise<{ data: Map<id, subsurface>, failures: [{ id, reason }] }>, where
// subsurface = { soil, bedrock, updatedAt } and either half may be null.

// ── Endpoints ───────────────────────────────────────────────────────────────
// Soil providers are tried in order. ISRIC's own REST service is canonical but
// has been paused for maintenance more than once; OpenEPI serves the same
// SoilGrids rasters through the same response shape, so it stands in when ISRIC
// is unavailable. Both are keyless.
const SOIL_PROPERTIES = ['sand', 'silt', 'clay'];
const SOIL_DEPTHS = ['0-5cm', '100-200cm'];

const SOIL_PROVIDERS = [
  {
    source: 'soilgrids-isric-v2',
    url: ({ lat, lng }) => 'https://rest.isric.org/soilgrids/v2.0/properties/query'
      + `?lat=${lat}&lon=${lng}`
      + SOIL_PROPERTIES.map(p => `&property=${p}`).join('')
      + SOIL_DEPTHS.map(d => `&depth=${d}`).join('')
      + '&value=mean',
  },
  {
    source: 'soilgrids-openepi',
    url: ({ lat, lng }) => 'https://api.openepi.io/soil/property'
      + `?lat=${lat}&lon=${lng}`
      + SOIL_PROPERTIES.map(p => `&properties=${p}`).join('')
      + SOIL_DEPTHS.map(d => `&depths=${d}`).join('')
      + '&values=mean',
  },
];

const BEDROCK_SOURCE = 'macrostrat';
const bedrockUrl = ({ lat, lng }) => `https://macrostrat.org/api/v2/geologic_units/map?lat=${lat}&lng=${lng}`;

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

// Every request is bounded. SoilGrids throttles hard, and an unbounded fetch
// against a throttled service is what turns a bulk run into a hang: one stalled
// connection would occupy a worker forever and the run would never finish.
const REQUEST_TIMEOUT_MS = 20000;
const round1 = (x) => (Number.isFinite(x) ? Math.round(x * 10) / 10 : null);
const backoffMs = (attempt) => Math.min(8000, 600 * 2 ** attempt) + Math.floor(Math.random() * 250);

// Shared JSON fetch with backoff, same posture as the climate archive helper:
// 429/5xx are transient and retried, anything else fails fast so the caller can
// fall through to the next provider rather than hammering a broken one.
async function fetchJson(url, retries = 2) {
  for (let attempt = 0; ; attempt++) {
    const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
    const timer = controller ? setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS) : null;
    let res;
    try {
      // eslint-disable-next-line no-await-in-loop
      res = await fetch(url, controller ? { signal: controller.signal } : undefined);
    } catch (e) {
      if (attempt >= retries) throw new Error(e?.name === 'AbortError' ? 'timed out' : 'network error');
      // eslint-disable-next-line no-await-in-loop
      await sleep(backoffMs(attempt));
      continue;
    } finally {
      if (timer) clearTimeout(timer);
    }
    if (res.status === 429 || res.status >= 500) {
      if (attempt >= retries) throw new Error(`service unavailable (HTTP ${res.status})`);
      const ra = Number(res.headers?.get?.('retry-after'));
      // eslint-disable-next-line no-await-in-loop
      await sleep(Number.isFinite(ra) && ra > 0 ? ra * 1000 : backoffMs(attempt));
      continue;
    }
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    try {
      // eslint-disable-next-line no-await-in-loop
      return await res.json();
    } catch {
      throw new Error('bad response');
    }
  }
}

// ── USDA texture triangle ───────────────────────────────────────────────────
// The standard twelve-class decision tree, applied to sand/silt/clay
// percentages. Returns null unless all three are present and sum to roughly
// 100 %, so a partial or nonsensical grid cell yields no class rather than a
// confident wrong one.
export function classifyTexture(sand, silt, clay) {
  if (![sand, silt, clay].every(v => Number.isFinite(v) && v >= 0)) return null;
  const total = sand + silt + clay;
  if (total < 90 || total > 110) return null;

  if (silt + 1.5 * clay < 15) return 'Sand';
  if (silt + 1.5 * clay >= 15 && silt + 2 * clay < 30) return 'Loamy sand';
  if ((clay >= 7 && clay < 20 && sand > 52 && silt + 2 * clay >= 30)
    || (clay < 7 && silt < 50 && silt + 2 * clay >= 30)) return 'Sandy loam';
  if (clay >= 7 && clay < 27 && silt >= 28 && silt < 50 && sand <= 52) return 'Loam';
  if ((silt >= 50 && clay >= 12 && clay < 27) || (silt >= 50 && silt < 80 && clay < 12)) return 'Silt loam';
  if (silt >= 80 && clay < 12) return 'Silt';
  if (clay >= 20 && clay < 35 && silt < 28 && sand > 45) return 'Sandy clay loam';
  if (clay >= 27 && clay < 40 && sand > 20 && sand <= 45) return 'Clay loam';
  if (clay >= 27 && clay < 40 && sand <= 20) return 'Silty clay loam';
  if (clay >= 35 && sand > 45) return 'Sandy clay';
  if (clay >= 40 && silt >= 40) return 'Silty clay';
  if (clay >= 40 && sand <= 45 && silt < 40) return 'Clay';
  return null;
}

// ── SoilGrids response → per-depth fractions ────────────────────────────────
// Both providers return SoilGrids' own structure:
//   properties.layers[] = { name, unit_measure: { d_factor }, depths: [...] }
//   depths[]            = { label: "0-5cm", values: { mean } }
// Values are stored scaled (clay/sand/silt come as g/kg, d_factor 10 → %), so
// the layer's own d_factor does the conversion; 10 is the documented default
// for the three texture fractions if a provider omits it.
const normalizeDepthLabel = (s) => String(s || '').replace(/\s+/g, '').toLowerCase();

export function parseSoilLayers(json) {
  const layers = json?.properties?.layers;
  if (!Array.isArray(layers) || !layers.length) return null;

  const byDepth = new Map(); // depth label → { sand, silt, clay }
  layers.forEach((layer) => {
    const name = String(layer?.name || '').toLowerCase();
    if (!SOIL_PROPERTIES.includes(name)) return;
    const factor = Number(layer?.unit_measure?.d_factor);
    const dFactor = Number.isFinite(factor) && factor > 0 ? factor : 10;
    (Array.isArray(layer?.depths) ? layer.depths : []).forEach((d) => {
      const label = normalizeDepthLabel(d?.label ?? d?.range?.label);
      const mean = Number(d?.values?.mean);
      if (!label || !Number.isFinite(mean)) return;
      const entry = byDepth.get(label) || {};
      entry[name] = mean / dFactor;
      byDepth.set(label, entry);
    });
  });
  return byDepth.size ? byDepth : null;
}

function depthSlice(byDepth, wanted) {
  const e = byDepth?.get(normalizeDepthLabel(wanted));
  if (!e) return null;
  const sand = round1(e.sand), silt = round1(e.silt), clay = round1(e.clay);
  // SoilGrids reports a cell it has no data for as zeros rather than nulls —
  // common on coastal and built-up sites. Writing 0/0/0 would look like a real
  // profile with an unclassifiable texture AND mark the site as done, so it
  // would never be retried. Treat it as no data for this depth instead.
  const values = [sand, silt, clay].filter(v => v != null);
  if (!values.length || values.every(v => v === 0)) return null;
  return { depth: wanted, sandPct: sand, siltPct: silt, clayPct: clay, texture: classifyTexture(sand, silt, clay) };
}

// ── Macrostrat response → bedrock unit ──────────────────────────────────────
// Lithology arrives as a string in Macrostrat's own notation —
// "Major:: {basalt,andesite} Minor:: {tuff}" — sometimes as a plain
// comma-separated list, and in other endpoints as an array of objects. Handle
// all three; majors come first so the primary lithology is the dominant rock.
export function parseLithologies(lith) {
  if (Array.isArray(lith)) {
    return [...new Set(lith
      .map(l => String(l?.name ?? l?.lith ?? l ?? '').trim().toLowerCase())
      .filter(Boolean))];
  }
  const text = String(lith || '').trim();
  if (!text) return [];

  const groups = [...text.matchAll(/(major|minor|indeterminate)::\s*\{([^}]*)\}/gi)];
  // Sources differ: some list "basalt,tuff", others
  // "flood basalt(s); mafic volcanic rocks; basalt". Split on both, or the
  // whole string becomes one "lithology" and the cell reads as a paragraph.
  const split = (s) => s.split(/[;,]/).map(x => x.trim().toLowerCase()).filter(Boolean);
  if (groups.length) {
    const rank = { major: 0, minor: 1, indeterminate: 2 };
    const ordered = groups
      .map(m => ({ rank: rank[m[1].toLowerCase()] ?? 3, names: split(m[2]) }))
      .sort((a, b) => a.rank - b.rank)
      .flatMap(g => g.names);
    return [...new Set(ordered)];
  }
  return [...new Set(split(text))];
}

export function parseBedrock(json) {
  const units = Array.isArray(json?.success?.data) ? json.success.data
    : (Array.isArray(json?.data) ? json.data : null);
  if (!units || !units.length) return null;

  // The first unit is the one mapped at the point; later entries are the
  // coarser maps stacked underneath it, so they are the natural fallback for a
  // field the top unit leaves blank.
  const withLith = units.find(u => parseLithologies(u?.lith).length) || units[0];
  const lithologies = parseLithologies(withLith?.lith);
  const name = String(withLith?.name || withLith?.strat_name || '').trim();
  const age = String(withLith?.best_int_name || withLith?.b_int_name || withLith?.t_int_name || '').trim();
  const tAge = Number(withLith?.t_age), bAge = Number(withLith?.b_age);
  const description = String(withLith?.descrip || '').trim();

  if (!lithologies.length && !name && !age) return null;
  return {
    lithology: lithologies[0] || null,
    lithologies,
    unit: name || null,
    age: age || null,
    ageRangeMa: (Number.isFinite(tAge) && Number.isFinite(bAge)) ? [tAge, bAge] : null,
    description: description || null,
    mapId: withLith?.map_id ?? null,
    source: BEDROCK_SOURCE,
  };
}

// ── Per-site lookups ────────────────────────────────────────────────────────
async function fetchSoil(point) {
  let lastError = 'no soil provider available';
  for (const provider of SOIL_PROVIDERS) {
    let json;
    try {
      // eslint-disable-next-line no-await-in-loop
      json = await fetchJson(provider.url(point));
    } catch (e) {
      lastError = e?.message || 'request failed';
      continue;
    }
    const byDepth = parseSoilLayers(json);
    const surface = depthSlice(byDepth, SOIL_DEPTHS[0]);
    const deep = depthSlice(byDepth, SOIL_DEPTHS[1]);
    if (!surface && !deep) { lastError = 'no soil data for this location'; continue; }
    return { soil: { surface, deep, source: provider.source, resolutionM: 250 } };
  }
  return { error: lastError };
}

async function fetchBedrock(point) {
  try {
    const bedrock = parseBedrock(await fetchJson(bedrockUrl(point)));
    return bedrock ? { bedrock } : { error: 'no mapped geologic unit here' };
  } catch (e) {
    return { error: e?.message || 'request failed' };
  }
}

// Returns { data: Map<id, subsurface>, failures: [{ id, reason }] }. A site is
// recorded when either half resolved — half the context is still context — and
// only a site with neither counts as a failure, with the reason from both
// services so the caller can tell "service down" from "nothing mapped here".
// `onResult({ id, subsurface, reason })` fires as each site settles, so a caller
// can persist and show progress while the run is still going. Without it a long
// run looks frozen and loses everything if the tab is closed midway.
export async function fetchSubsurface(points, { concurrency = 3, onResult } = {}) {
  const valid = (points || []).filter(p => Number.isFinite(p.lat) && Number.isFinite(p.lng));
  const data = new Map();
  const failures = [];
  if (!valid.length) return { data, failures };

  const updatedAt = new Date().toISOString().slice(0, 10);
  const queue = [...valid];

  const worker = async () => {
    while (queue.length) {
      const p = queue.shift();
      // eslint-disable-next-line no-await-in-loop
      const [soilRes, rockRes] = await Promise.all([fetchSoil(p), fetchBedrock(p)]);
      if (!soilRes.soil && !rockRes.bedrock) {
        const reason = `soil: ${soilRes.error}; bedrock: ${rockRes.error}`;
        failures.push({ id: p.id, reason });
        if (onResult) {
          // eslint-disable-next-line no-await-in-loop
          await onResult({ id: p.id, subsurface: null, reason });
        }
        continue;
      }
      const subsurface = {
        soil: soilRes.soil || null,
        bedrock: rockRes.bedrock || null,
        updatedAt,
      };
      data.set(p.id, subsurface);
      if (onResult) {
        // eslint-disable-next-line no-await-in-loop
        await onResult({ id: p.id, subsurface, reason: null });
      }
    }
  };
  await Promise.all(Array.from({ length: Math.min(concurrency, valid.length) }, worker));
  return { data, failures };
}

const sentenceCase = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : '');

// ── Plain rock names ────────────────────────────────────────────────────────
// Geological maps name map UNITS, not rocks: "flood basalt(s)", "mafic volcanic
// rocks", "Mesozoic-Cenozoic volcanic rocks". Architects want the rock —
// basalt, granite, schist. Often the map's own term list already contains it
// (the Deccan sites come back as ["flood basalt(s)", "mafic volcanic rocks",
// "basalt"]), so prefer the plainest rock name the source actually gave.
//
// Nothing here invents specificity the map did not have: where the map only
// says "crystalline metamorphic rocks", we say "Metamorphic rock", never
// "schist". The raw terms stay in the record and are shown on hover.
const ROCK_TERMS = [
  'laterite', 'basalt', 'dolerite', 'gabbro', 'granite', 'granodiorite', 'diorite',
  'rhyolite', 'andesite', 'tuff', 'pumice', 'obsidian',
  'gneiss', 'schist', 'phyllite', 'slate', 'quartzite', 'marble', 'amphibolite',
  'sandstone', 'limestone', 'dolomite', 'shale', 'siltstone', 'mudstone', 'claystone',
  'conglomerate', 'breccia', 'chert', 'chalk', 'coal', 'laterite',
  'alluvium', 'sand and gravel', 'sand', 'gravel', 'silt', 'clay', 'peat',
];

// Fallbacks when the map named a family rather than a rock. Order matters: the
// first match wins, so compound families come before their parts.
const ROCK_FAMILIES = [
  [/sedimentary and volcanic/, 'Sedimentary & volcanic rock'],
  [/metamorphic/, 'Metamorphic rock'],
  [/carbonate/, 'Limestone'],
  [/plutonic|intrusive/, 'Intrusive igneous rock'],
  [/volcanic|mafic|igneous/, 'Volcanic rock'],
  [/sedimentary/, 'Sedimentary rock'],
];

// Returns { primary, all } — plain rock names found in the map's own terms,
// most specific first. Both are null/empty when the map named nothing usable.
export function plainLithology(lithologies) {
  const terms = (Array.isArray(lithologies) ? lithologies : [lithologies])
    .filter(Boolean).map(t => String(t).toLowerCase());
  if (!terms.length) return { primary: null, all: [] };

  const found = [];
  ROCK_TERMS.forEach((rock) => {
    if (found.includes(rock)) return;
    // Word-boundary match so "sand" doesn't fire on "sandstone".
    const re = new RegExp(`(^|[^a-z])${rock.replace(/ /g, '\\s+')}($|[^a-z])`);
    if (terms.some(t => re.test(t))) found.push(rock);
  });
  if (found.length) return { primary: sentenceCase(found[0]), all: found };

  for (const [pattern, label] of ROCK_FAMILIES) {
    if (terms.some(t => pattern.test(t))) return { primary: label, all: [] };
  }
  // Nothing recognised: tidy the map's own wording rather than drop it.
  return { primary: sentenceCase(terms[0].replace(/\(s\)/g, '').trim()), all: [] };
}

// ── Display helpers, shared by the overview table and its detail card ───────
// "Sandy clay loam" for the shallow soil, falling back to the deep slice.
export function soilLabel(subsurface) {
  const s = subsurface?.soil;
  if (!s) return null;
  return s.surface?.texture || s.deep?.texture || null;
}

// "Basalt · Late Cretaceous" — the rock in plain terms, then its age.
export function bedrockLabel(subsurface) {
  const b = subsurface?.bedrock;
  if (!b) return null;
  const rock = plainLithology(b.lithologies?.length ? b.lithologies : b.lithology).primary;
  const parts = [rock || b.unit, b.age].filter(Boolean);
  return parts.length ? parts.join(' · ') : null;
}

// What the geological map itself said, for the hover: the plain name is our
// reading of these terms, so they stay one hover away rather than being lost.
export function bedrockSourceTerms(subsurface) {
  const b = subsurface?.bedrock;
  const terms = b?.lithologies?.length ? b.lithologies : (b?.lithology ? [b.lithology] : []);
  return terms.length ? `Mapped as: ${terms.join('; ')}` : null;
}

// ── Human overrides ─────────────────────────────────────────────────────────
// projects.subsurface_override holds what the firm knows and the datasets
// cannot: laterite is the standing example — a weathering profile ON the
// bedrock, so a geological map classifies a Konkan site as the basalt beneath
// it. An override wins wherever a reading is displayed, survives every
// re-fetch (it lives in its own column), and clearing it falls straight back to
// the mapped value.
//
// resolveSoil / resolveBedrock → { text, manual }: `manual` is what tells the
// UI to mark the cell and lets the tooltip still show what the map said.
const trimmed = (v) => (typeof v === 'string' ? v.trim() : '');

export function resolveSoil(subsurface, override) {
  const manual = trimmed(override?.soil);
  if (manual) return { text: manual, manual: true };
  return { text: soilLabel(subsurface), manual: false };
}

export function resolveBedrock(subsurface, override) {
  const manual = trimmed(override?.bedrock);
  if (manual) return { text: manual, manual: true };
  return { text: bedrockLabel(subsurface), manual: false };
}

// Merge one edited key into an override blob, dropping the blob entirely once
// nothing is left — an empty object would keep the row looking overridden.
export function withOverride(override, key, value) {
  const next = { ...(override || {}) };
  const text = trimmed(value);
  if (text) next[key] = text; else delete next[key];
  delete next.updatedAt;
  if (!Object.keys(next).length) return null;
  return { ...next, updatedAt: new Date().toISOString().slice(0, 10) };
}

// One-line composition of a depth slice: "0-5cm: sandy clay loam (52 % sand,
// 20 % silt, 28 % clay)". Used in tooltips and the detail card.
export function describeSoilSlice(slice) {
  if (!slice) return null;
  const pct = (v) => (v == null ? '–' : `${Math.round(v)} %`);
  const head = slice.texture ? `${slice.depth}: ${slice.texture.toLowerCase()}` : `${slice.depth}`;
  return `${head} (${pct(slice.sandPct)} sand, ${pct(slice.siltPct)} silt, ${pct(slice.clayPct)} clay)`;
}
